import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const useIsomorphicLayoutEffect = typeof window !== "undefined" ? useLayoutEffect : useEffect;
  useIsomorphicLayoutEffect(() => {
    const node = ref.current;
    if (!node || typeof window === "undefined") return;
    const rect = node.getBoundingClientRect();
    const alreadyVisible = rect.top < window.innerHeight && rect.bottom > 0 && rect.left < window.innerWidth && rect.right > 0;
    if (alreadyVisible) {
      setIsVisible(true);
    }
  }, []);
  useEffect(() => {
    const node = ref.current;
    if (!node || once && isVisible) return;
    if (typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) observer.unobserve(entry.target);
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [once, rootMargin, threshold, isVisible]);
  return {
    ref,
    isVisible
  };
}
const timelineSteps = [{
  num: "01",
  title: "Escuchamos y medimos",
  desc: "Revisamos sus canales de atención y encontramos dónde se pierden dinero, tiempo y clientes."
}, {
  num: "02",
  title: "Priorizamos lo que más le cuesta",
  desc: "Elegimos primero lo que más ahorro y mejor servicio le da, sin complicar su operación."
}, {
  num: "03",
  title: "Conectamos sus canales y sistemas",
  desc: "Unimos WhatsApp, llamadas, chat, correo y sus herramientas en un solo lugar."
}, {
  num: "04",
  title: "Automatizamos con IA",
  desc: "La IA responde lo repetido y su equipo atiende lo importante, con respuestas rápidas y personalizadas."
}, {
  num: "05",
  title: "Medimos y mejoramos",
  desc: "Revisamos los resultados con usted y ajustamos para que el ahorro y el servicio sigan mejorando."
}];
function TimelineItem({
  step,
  index,
  "data-index": dataIndex
}: {
  step: (typeof timelineSteps)[0];
  index: number;
  "data-index": number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.3,
    once: false
  });
  const isEven = index % 2 === 0;
  return <div ref={ref} data-index={dataIndex} className="relative flex flex-col md:flex-row items-start md:items-center w-full mb-16 md:mb-20 last:mb-0 group">
      {/* Mobile dot */}
      <div className="absolute left-[15px] top-6 w-3.5 h-3.5 bg-primary -ml-[7px] md:hidden transition-transform duration-500 ease-out z-10 rounded-full border-2 border-background" style={{
      transform: isVisible ? "scale(1.4)" : "scale(1)"
    }} />

      {/* Desktop dot */}
      <div className="hidden md:block absolute left-1/2 top-1/2 w-4 h-4 bg-primary -translate-x-1/2 -translate-y-1/2 transition-transform duration-500 ease-out z-10 rounded-full border-2 border-background" style={{
      transform: `translate(-50%, -50%) scale(${isVisible ? 1.4 : 1})`
    }} />

      {/* Content card */}
      <div className={`w-full md:w-1/2 pl-10 md:pl-0 ${isEven ? "md:pr-14 md:text-right" : "md:order-2 md:pl-14 md:pr-0"}`}>
        <div className={`transition-all duration-500 ease-out p-6 md:p-8 rounded-md bg-card border border-border shadow-sm hover:border-primary/50 hover:shadow-md ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <span className="text-primary font-default text-3xl md:text-4xl font-bold tracking-tight block mb-2 opacity-80">
            {step.num}
          </span>
          <h3 className="text-xl md:text-2xl font-default font-bold mb-3 text-card-foreground tracking-tight">
            {step.title}
          </h3>
          <p className="text-muted-foreground font-default text-base leading-relaxed max-w-md ml-0 md:ml-auto">
            {step.desc}
          </p>
        </div>
      </div>

      <div className={`hidden md:block w-1/2 ${isEven ? "order-2" : ""}`} />
    </div>;
}
export default function CMoFuncionaElProceso() {
  const [progress, setProgress] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleScroll = () => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const start = windowHeight / 2;
      const totalHeight = rect.height;
      const scrolled = start - rect.top;
      let percentage = scrolled / totalHeight * 100;
      percentage = Math.max(0, Math.min(100, percentage));
      setProgress(percentage);
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  return <section data-section-id="265010" id="como-trabajamos" className="bg-background text-foreground py-16 md:py-24 overflow-hidden">
      <div className="container mx-auto px-4 md:px-6 max-w-5xl">
        <div className="text-center mb-16 md:mb-20 max-w-3xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-default font-bold text-foreground tracking-tight mb-4">
            Cómo trabajamos
          </h2>
          <p className="text-muted-foreground font-default text-lg">
            Empezamos por identificar dónde su empresa pierde más tiempo y dinero. Solo después automatizamos.
          </p>
        </div>

        <div className="relative mb-16 md:mb-20" ref={containerRef}>
          {/* Static track */}
          <div className="absolute top-0 bottom-0 left-[15px] md:left-1/2 w-[2px] bg-border -ml-[1px]" />

          {/* Progress fill */}
          <div className="absolute top-0 left-[15px] md:left-1/2 w-[2px] bg-secondary -ml-[1px] origin-top transition-all duration-100 ease-out z-0" style={{
          height: `${progress}%`
        }} />

          <div className="relative z-10 py-4">
            {timelineSteps.map((step, index) => <TimelineItem key={index} step={step} index={index} data-index={index} />)}
          </div>
        </div>

        {/* Centered Yellow CTA */}
        <div className="flex justify-center text-center">
          <Link to="#contacto" className="inline-flex items-center justify-center rounded-md bg-chart-4 hover:bg-chart-4/90 text-black font-default font-bold px-8 py-4 text-base md:text-lg shadow-md hover:shadow-lg transition-all">
            Descubra dónde pierde dinero
            <ArrowRight className="ml-2 h-5 w-5" />
          </Link>
        </div>
      </div>
    </section>;
}